import React from 'react';
import { TrendingUp, TrendingDown, Target } from 'lucide-react';

/**
 * GoalProgressBar - Visual progress indicator for goals
 *
 * Props:
 * - progress: number (0-100+)
 * - direction: 'maximize' | 'minimize' | 'target'
 * - isOnTrack: boolean
 * - size: 'sm' | 'md' | 'lg'
 * - showLabel: boolean
 * - animate: boolean
 */
const GoalProgressBar = ({
  progress = 0,
  direction = 'maximize',
  isOnTrack = true,
  size = 'md',
  showLabel = true,
  animate = true,
}) => {
  // Clamp progress to 0-100 for display
  const displayProgress = Math.min(Math.max(progress, 0), 100);

  // Determine color based on progress and on-track status
  const getProgressColor = () => {
    if (progress >= 100) return 'bg-green-500';
    if (!isOnTrack) return 'bg-yellow-500';
    if (progress >= 75) return 'bg-green-400';
    if (progress >= 50) return 'bg-blue-500';
    if (progress >= 25) return 'bg-yellow-400';
    return 'bg-red-400';
  };

  // Size configurations
  const sizeConfig = {
    sm: {
      bar: 'h-1.5',
      text: 'text-xs',
      icon: 'h-3 w-3',
    },
    md: {
      bar: 'h-2',
      text: 'text-sm',
      icon: 'h-4 w-4',
    },
    lg: {
      bar: 'h-3',
      text: 'text-base',
      icon: 'h-5 w-5',
    },
  };

  const config = sizeConfig[size];

  // Direction icon
  const DirectionIcon = () => {
    switch (direction) {
      case 'maximize':
        return <TrendingUp className={`${config.icon} text-green-600`} />;
      case 'minimize':
        return <TrendingDown className={`${config.icon} text-blue-600`} />;
      case 'target':
        return <Target className={`${config.icon} text-purple-600`} />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-1">
            <DirectionIcon />
            <span className={`${config.text} font-medium text-gray-700`}>
              {direction === 'maximize' && 'Maximize'}
              {direction === 'minimize' && 'Minimize'}
              {direction === 'target' && 'Target'}
            </span>
          </div>
          <span className={`${config.text} font-semibold ${
            progress >= 100 ? 'text-green-600' :
            !isOnTrack ? 'text-yellow-600' : 'text-gray-700'
          }`}>
            {progress.toFixed(0)}%
          </span>
        </div>
      )}
      <div className={`w-full bg-gray-200 rounded-full ${config.bar} overflow-hidden`}>
        <div
          className={`${config.bar} rounded-full ${getProgressColor()} ${
            animate ? 'transition-all duration-500 ease-out' : ''
          }`}
          style={{ width: `${displayProgress}%` }}
        />
      </div>
    </div>
  );
};

/**
 * CircularProgressBar - Circular progress indicator for compact displays
 */
export const CircularGoalProgress = ({
  progress = 0,
  direction = 'maximize',
  isOnTrack = true,
  size = 60,
  strokeWidth = 6,
}) => {
  const displayProgress = Math.min(Math.max(progress, 0), 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (displayProgress / 100) * circumference;

  const getStrokeColor = () => {
    if (progress >= 100) return '#22c55e'; // green-500
    if (!isOnTrack) return '#eab308'; // yellow-500
    if (progress >= 75) return '#4ade80'; // green-400
    if (progress >= 50) return '#3b82f6'; // blue-500
    return '#f87171'; // red-400
  };

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        className="transform -rotate-90"
        width={size}
        height={size}
      >
        {/* Background circle */}
        <circle
          className="text-gray-200"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        {/* Progress circle */}
        <circle
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke={getStrokeColor()}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          className="transition-all duration-500 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-gray-700">
          {displayProgress.toFixed(0)}%
        </span>
      </div>
    </div>
  );
};

export default GoalProgressBar;
